/* Authored lifeguard atlas on the photograph's shared ground plane.
 * No animation clock runs here: the coast controller owns time.
 * Standing-flag ownership transfers at PICKUPS[n].grasp, without a position jump.
 */
(() => {
  'use strict';
  if (window.CgauCoastalPatrol) { window.CgauCoastalPatrol.scan(document); return; }
  const W = 1672, H = 941, GROUND = H * .85, EYE = 1.6, TAU = Math.PI * 2;
  const PICKUPS = [{start:.325, grasp:.332, end:.35}, {start:.435, grasp:.447, end:.47}];
  const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
  const lerp = (a,b,t) => a+(b-a)*t;
  const smooth = x => { x=clamp(x); return x*x*(3-2*x); };
  const mix = (a,b,t) => [lerp(a[0],b[0],t),lerp(a[1],b[1],t)];
  const point = (x,y) => [x,y];
  const length = (a,b) => Math.hypot(a[0]-b[0],a[1]-b[1]);
  const instances = new Map();
  const SKIN = Object.freeze({shadow:'#5d5142',rim:'#a58865'});

  function flagStanding(index, phase, mode = 'cycle') {
    if (mode === 'day' || mode === 'sunrise') return true;
    if (mode !== 'cycle') return false;
    return Number(phase) < PICKUPS[index === 1 ? 1 : 0].grasp;
  }
  // The elbow/knee is the intersection of two bone-length circles. Keeping
  // the target separate makes hand-to-pole and sole-to-ground contacts exact.
  function joint(a, b, upper, lower, bend) {
    const d = Math.max(.001, length(a,b));
    const reach = clamp(d, Math.abs(upper-lower)+.001, upper+lower-.001);
    const along = (upper*upper-lower*lower+reach*reach)/(2*reach);
    const perpendicular = Math.sqrt(Math.max(0,upper*upper-along*along))*bend;
    const ux=(b[0]-a[0])/d, uy=(b[1]-a[1])/d;
    return [a[0]+ux*along-uy*perpendicular,a[1]+uy*along+ux*perpendicular];
  }
  function geometry(horizon = .507) {
    const ppm = (GROUND-H*horizon)/EYE;
    return {ppm, height:1.75*ppm, poleHeight:2.4*ppm, flagWidth:.75*ppm,
      gripHeight:1.25*ppm, ground:GROUND, flags:[W*.10,W*.82]};
  }
  // Constant-velocity middle with short ease-in/out for the planted pickup.
  function walkProgress(t, ramp=.09) {
    t=clamp(t); const total=1-ramp;
    if(t<ramp) return t*t/(2*ramp*total);
    if(t>1-ramp) return 1-(1-t)*(1-t)/(2*ramp*total);
    return (t-ramp/2)/total;
  }
  function sample(phase, mode='cycle', horizon=.507) {
    const g=geometry(horizon), h=g.height;
    // Stand 35cm behind the pole so the lifting hand never folds tightly into
    // the shoulder as it passes chest height. Gait and ground scale stay fixed.
    const first=g.flags[0]-.20*h, second=g.flags[1]-.20*h;
    let x=-.72*h, action='absent', walking=false, travelStart=x, travelEnd=first;
    if(mode !== 'cycle' || phase<.30 || phase>=.51) return {active:false, action, ...g, phase, standing:[flagStanding(0,phase,mode),flagStanding(1,phase,mode)]};
    let progress=0;
    if(phase<.325) { action='arriving'; progress=(phase-.30)/.025; x=lerp(travelStart,first,walkProgress(progress)); walking=true; }
    else if(phase<.35) { action='first-pickup'; x=first; }
    else if(phase<.435) { action='crossing'; travelStart=first; travelEnd=second; progress=(phase-.35)/.085; x=lerp(first,second,walkProgress(progress)); walking=true; }
    else if(phase<.47) { action='second-pickup'; x=second; }
    else { action='departing'; travelStart=second; travelEnd=W+.86*h; progress=(phase-.47)/.04; x=lerp(second,travelEnd,walkProgress(progress,.04)); walking=true; }
    const pickupIndex=action==='first-pickup'?0:action==='second-pickup'?1:-1;
    const pickup=pickupIndex<0?null:PICKUPS[pickupIndex];
    const pickupT=pickup?smooth((phase-pickup.start)/(pickup.end-pickup.start)):0;
    const bend=pickup?Math.sin(pickupT*Math.PI)*.008*h:0;
    const walkWeight=walking ? smooth(Math.min(x-travelStart,travelEnd-x)/(.16*h)) : 0;
    const stride=1.28*g.ppm, distance=x-first;
    const gait=((distance/stride)%1+1)%1;
    const feet=[];
    for(let leg=0;leg<2;leg++) {
      const t=(gait+leg*.5)%1;
      const swing=clamp((t-.6)/.4);
      const relative=t<.6 ? .25*stride-t*stride : -.35*stride+.6*stride*smooth(swing);
      const standing=(leg===0?-.048:.048)*h;
      const anchor=x+lerp(standing,relative,walkWeight);
      const lift=Math.sin(swing*Math.PI)*.065*h*walkWeight;
      let pitch=t<.15 ? 15*(1-smooth(t/.15)) : t<.44 ? 0 : t<.6 ? -35*smooth((t-.44)/.16)
        : -35*(1-smooth(swing/.65))+15*smooth((swing-.50)/.50);
      pitch*=walkWeight;
      const sole=.038*h,heel=-.028*h,toe=.064*h;
      const pivot=pitch>=0?heel:toe,angle=-pitch*Math.PI/180,cos=Math.cos(angle),sin=Math.sin(angle);
      const ankle=point(anchor+pivot-(pivot*cos-sole*sin),GROUND-lift-(pivot*sin+sole*cos));
      const local=[[heel,sole],[toe,sole],[.074*h,.030*h],[.065*h,.015*h],[.047*h,.006*h],[.016*h,-.003*h],[-.008*h,-.010*h],[-.020*h,.004*h],[-.032*h,.011*h]];
      const transform=([px,py])=>point(ankle[0]+px*cos-py*sin,ankle[1]+px*sin+py*cos);
      feet.push({foot:point(anchor,GROUND-lift),ankle,pitch,angle,local,outline:local.map(transform),
        contact:point(anchor+pivot,GROUND-lift),planted:!walking||walkWeight<.01||t<.6,gait:t});
    }
    // The longest planted reach sets hip height. Taking the lower of the two
    // admissible heights during double support prevents either leg stretching.
    // With 24.5% thigh/shin, 3.8% ankle height and 177-degree extension, standing
    // hip height is about 52.7% of stature; mid-stance passes near straight.
    const legLength=.245*h,maxReach=2*legLength*Math.sin(177*Math.PI/360);
    const hipY=Math.max(...feet.map((f,index)=>{
      const dx=x+(index?1:-1)*h*.006-f.ankle[0];
      return f.ankle[1]-Math.sqrt(Math.max(.001,maxReach*maxReach-dx*dx));
    }))+bend;
    const hip=point(x,hipY);
    const shoulder=point(x+lerp(.010,.030,walkWeight)*h+bend*.15,hipY-.291*h);
    const rods=[];
    for(let index=0;index<2;index++) {
      const pick=PICKUPS[index];
      if(phase<pick.grasp) { rods.push(null); continue; }
      const local=clamp((phase-pick.grasp)/(pick.end-pick.grasp));
      const lift=smooth(local/.38), rotate=smooth((local-.38)/.62);
      const carryAxis=point(Math.sin(55*Math.PI/180),-Math.cos(55*Math.PI/180));
      const shoulderContact=point(shoulder[0]+(index?.010:-.010)*h,shoulder[1]-(index?.007:0)*h);
      const carry=point(shoulderContact[0]+carryAxis[0]*.21*h,shoulderContact[1]+carryAxis[1]*.21*h);
      const lifted=point(g.flags[index],GROUND-g.gripHeight-.5*g.ppm*lift);
      const grip=mix(lifted,carry,rotate);
      const angle=55*Math.PI/180*rotate;
      const axis=point(Math.sin(angle),-Math.cos(angle));
      const base=point(grip[0]-axis[0]*g.gripHeight,grip[1]-axis[1]*g.gripHeight);
      const top=point(base[0]+axis[0]*g.poleHeight,base[1]+axis[1]*g.poleHeight);
      rods.push({index,grip,base,top,angle,progress:local,shoulderContact});
    }
    // First pole stays in the far hand throughout; the free near hand collects
    // the second. There is no instantaneous transfer between shoulders.
    const hands=[null,null];
    for(let index=0;index<2;index++) {
      const arm=index===0?0:1, pick=PICKUPS[index];
      const swing=-Math.cos((gait+arm*.5)*TAU);
      const rest=point(shoulder[0]+.018*h+swing*.075*h*walkWeight,shoulder[1]+.343*h);
      if(rods[index]) hands[arm]=rods[index].grip;
      else if(pickupIndex===index) hands[arm]=mix(rest,point(g.flags[index],GROUND-g.gripHeight),smooth((phase-pick.start)/(pick.grasp-pick.start)));
      else hands[arm]=rest;
    }
    const arms=hands.map((hand,index)=>{
      const root=point(shoulder[0]+(index?1:-1)*h*.027,shoulder[1]+h*.020);
      const upper=.185*h,lower=.177*h;
      // The forward/down solution bends naturally below the hand during carry.
      // One branch is used throughout the reach, lift and carry to avoid a flip.
      const elbow=joint(root,hand,upper,lower,1);
      return {root,elbow,hand,upper,lower,material:SKIN};
    });
    const legs=feet.map((f,index)=>{
      const root=point(hip[0]+(index?1:-1)*h*.006,hip[1]);
      const ankle=f.ankle,knee=joint(root,ankle,legLength,legLength,-1);
      return {root,knee,ankle,foot:f.foot,footPose:f,upper:legLength,lower:legLength,material:SKIN};
    });
    return {active:true,action,phase,x,walking,walkWeight,gait,hip,shoulder,feet,hands,arms,legs,rods,bend,
      ...g, standing:[flagStanding(0,phase,mode),flagStanding(1,phase,mode)]};
  }

  class Patrol {
    constructor(scene) {
      this.scene=scene; this.section=scene.closest('[data-coastal-section]'); this.image=scene.querySelector('[data-coast-image]');
      this.canvas=document.createElement('canvas'); this.canvas.className='coastal-patrol-canvas';
      this.canvas.setAttribute('aria-hidden','true');
      // Explicit opacity avoids the base ocean-canvas loading rule. No pointer
      // target, page-level styles, external files or per-frame DOM are added.
      Object.assign(this.canvas.style,{position:'absolute',inset:'0',width:'100%',height:'100%',pointerEvents:'none',zIndex:'3',opacity:'1'});
      scene.append(this.canvas); this.ctx=this.canvas.getContext('2d',{alpha:true});
      this.assetState='unrequested';this.atlases=[];this.loadVersion=0;this.resizePending=true;
      this.scene.dataset.patrolRenderer='atlas';this.scene.dataset.patrolReady='unrequested';
      this.cleanups=[]; this.raf=0; this.frames=0; this.pending=true; this.lastDraw=-Infinity;
      this.phase=Number(scene.dataset.phase)||.25; this.night=Number(scene.dataset.night)||0;
      this.mode=this.section?.dataset.mode||scene.dataset.coastMode||'day';
      this.reduce=matchMedia('(prefers-reduced-motion: reduce)'); this.override=false;
      this.userPaused=this.section?.dataset.paused==='true'; this.ambient=this.section?.dataset.cgauMotionPaused==='true';
      this.rendererState=scene.dataset.motion||''; this.suspended=false; this.destroyed=false;
      const box=scene.getBoundingClientRect(); this.visible=box.bottom>0&&box.top<innerHeight;
      this.listen(scene,'coast:time',event=>{
        const d=event.detail||{}, oldMode=this.mode;
        if(Number.isFinite(d.phase)) this.phase=d.phase;
        if(Number.isFinite(d.night)) this.night=clamp(d.night);
        this.mode=d.mode||this.section?.dataset.mode||this.mode;
        const inSequence=this.mode==='cycle'&&this.phase>=.30&&this.phase<.51;
        this.pending=this.pending||inSequence||this.rendered===true||this.mode!==oldMode;
        // An unrelated running clock cannot animate a reduced-motion still.
        if(!this.frozen()||d.still===true||this.mode!==oldMode) this.request();
      });
      this.listen(scene,'coast:pause',event=>{
        if(typeof event.detail?.paused!=='boolean')return;
        this.userPaused=event.detail.paused;
        if(this.userPaused)this.override=false;
        else if(event.detail.userInitiated===true)this.override=true;
        this.pending=true; this.sync();
      });
      this.listen(scene,'coast:motion',event=>{ this.rendererState=event.detail?.state||''; this.sync(); });
      const ambient=event=>{ if(typeof event.detail?.paused==='boolean')this.ambient=event.detail.paused; this.sync(); };
      if(this.section)this.listen(this.section,'cgau:ambient-motion-change',ambient);
      this.listen(window,'cgau:ambient-motion-change',event=>{if(event.target===window)ambient(event);});
      this.listen(document,'visibilitychange',()=>this.sync());
      this.listen(this.reduce,'change',()=>{this.override=false;this.pending=true;this.sync();});
      if(this.image)this.listen(this.image,'load',()=>this.resize());
      if('IntersectionObserver' in window){this.io=new IntersectionObserver(entries=>{this.visible=entries[0].isIntersecting;this.sync();},{threshold:0});this.io.observe(scene);}
      if('ResizeObserver' in window){this.ro=new ResizeObserver(()=>this.resize());this.ro.observe(scene);}
      else this.listen(window,'resize',()=>this.resize());
      this.resize(); this.sync();
    }
    listen(target,type,fn){target.addEventListener(type,fn);this.cleanups.push(()=>target.removeEventListener(type,fn));}
    reduced(){return this.reduce.matches&&!this.override;}
    frozen(){return this.userPaused||this.reduced()||['paused','reduced'].includes(this.rendererState);}
    drawable(){return this.ctx&&!this.destroyed&&!this.suspended&&!document.hidden&&this.visible&&!this.ambient&&this.width>0&&this.height>0&&!['hidden','offscreen','ambient-paused'].includes(this.rendererState);}
    async loadAssets(){
      if(this.assetState!=='unrequested'||!this.drawable()||this.reduced())return;
      const source=this.scene.dataset.patrolManifest;
      if(!source){this.assetState='unavailable';this.scene.dataset.patrolReady='unavailable';return;}
      const version=++this.loadVersion;
      this.assetState='loading';this.scene.dataset.patrolReady='loading';
      this.loadAbort=new AbortController();
      const timeout=setTimeout(()=>this.loadAbort?.abort(),20000);
      try{
        const url=new URL(source,document.baseURI);
        if(!['http:','https:'].includes(url.protocol))throw new Error('manifest-url');
        const response=await fetch(url.href,{signal:this.loadAbort.signal,credentials:'same-origin'});
        if(!response.ok)throw new Error('manifest-response');
        const data=await response.text();if(data.length>4000000)throw new Error('manifest-size');
        const m=JSON.parse(data),pair=q=>Array.isArray(q)&&q.length===2&&q.every(Number.isFinite);
        if(m.version!=='1'||!Number.isInteger(m.width)||!Number.isInteger(m.height)||m.width<1||m.height<1||m.width>1024||m.height>1024||!Number.isFinite(m.metresPerPixel)||m.metresPerPixel<=0||m.metresPerPixel>.1||!pair(m.origin)||!Array.isArray(m.atlases)||!m.atlases.length||m.atlases.length>12||!Array.isArray(m.frames)||!m.frames.length||m.frames.length>2000)throw new Error('manifest-contract');
        if(m.strideMetres!==undefined&&(!Number.isFinite(m.strideMetres)||m.strideMetres<=0||m.strideMetres>3))throw new Error('manifest-stride');
        for(const name of ['walk0','walk1','walk2','pickup1','pickup2','idle0','idle1','idle2','stop0','start1','stop1','start2']){
          const ids=m.clips?.[name];if(!Array.isArray(ids)||!ids.length||!ids.every(id=>Number.isInteger(id)&&id>=0&&id<m.frames.length))throw new Error('manifest-clips');
        }
        if(!m.frames.every(f=>Number.isInteger(f.atlas)&&f.atlas>=0&&f.atlas<m.atlases.length&&Number.isInteger(f.x)&&Number.isInteger(f.y)&&f.x>=0&&f.y>=0&&Array.isArray(f.hands)&&f.hands.length===2&&f.hands.every(pair)&&Array.isArray(f.feet)&&f.feet.length===2&&f.feet.every(pair)&&Array.isArray(f.poleAngles)&&f.poleAngles.length===2&&f.poleAngles.every(a=>a===null||Number.isFinite(a))))throw new Error('manifest-frames');
        if(!m.frames.every(f=>f.crop===undefined||(Array.isArray(f.crop)&&f.crop.length===4&&f.crop.every(Number.isInteger)&&f.crop[0]>=0&&f.crop[1]>=0&&f.crop[2]>0&&f.crop[3]>0&&f.crop[0]+f.crop[2]<=m.width&&f.crop[1]+f.crop[3]<=m.height)))throw new Error('manifest-crop');
        this.assetImages=[];
        const atlases=await Promise.all(m.atlases.map(path=>new Promise((resolve,reject)=>{
          if(typeof path!=='string'){reject(new Error('atlas-url'));return;}
          const address=new URL(path,url);if(!['http:','https:'].includes(address.protocol)){reject(new Error('atlas-url'));return;}
          const image=new Image();this.assetImages.push(image);
          const signal=this.loadAbort.signal;
          const done=()=>{image.onload=null;image.onerror=null;signal.removeEventListener('abort',abort);};
          const abort=()=>{done();image.src='';reject(new Error('atlas-aborted'));};
          image.onload=()=>{done();if(image.naturalWidth<1||image.naturalHeight<1||image.naturalWidth*image.naturalHeight>16777216)reject(new Error('atlas-size'));else resolve(image);};
          image.onerror=()=>{done();reject(new Error('atlas-image'));};
          if(signal.aborted){abort();return;}signal.addEventListener('abort',abort,{once:true});
          image.decoding='async';image.src=address.href;
        })));
        if(!m.frames.every(f=>f.x+(f.crop?.[2]??m.width)<=atlases[f.atlas].naturalWidth&&f.y+(f.crop?.[3]??m.height)<=atlases[f.atlas].naturalHeight))throw new Error('atlas-frame-bounds');
        if(this.destroyed||version!==this.loadVersion)return;
        this.manifest=m;this.atlases=atlases;this.assetState='ready';this.scene.dataset.patrolReady='ready';
      }catch(error){
        if(this.destroyed||version!==this.loadVersion)return;
        this.assetState='error';this.scene.dataset.patrolReady='error';this.scene.dataset.patrolError=error instanceof Error?error.message:'asset-load';
        this.loadAbort.abort();this.atlases=[];
      }finally{
        clearTimeout(timeout);
        if(!this.destroyed&&version===this.loadVersion){this.pending=true;this.lastDraw=-Infinity;this.sync();this.scene.dispatchEvent(new CustomEvent('coast:patrol-ready',{detail:{ready:this.assetState==='ready'}}));}
      }
    }
    sync(){
      if(this.destroyed)return;
      const state=!this.ctx?'unavailable':this.suspended||document.hidden?'hidden':!this.visible?'offscreen':this.ambient?'ambient-paused':this.reduced()?'reduced':this.frozen()?'paused':this.drawable()?'running':this.rendererState;
      this.scene.dataset.patrolState=state;this.canvas.dataset.patrolState=state;
      if(!this.drawable()){cancelAnimationFrame(this.raf);this.raf=0;}else{this.loadAssets();if(this.pending)this.request();}
    }
    request(){if(!this.raf&&this.pending&&this.drawable())this.raf=requestAnimationFrame(now=>{this.raf=0;if(!this.drawable())return;if(!this.frozen()&&now-this.lastDraw<32)return;this.lastDraw=now;this.draw();});}
    resize(){
      if(!this.ctx||this.destroyed)return;
      const box=this.scene.getBoundingClientRect();this.width=Math.max(0,box.width);this.height=Math.max(0,box.height);
      this.resizePending=true;this.pending=true;this.lastDraw=-Infinity;this.sync();
    }
    applySize(){
      if(!this.drawable())return;
      this.dpr=Math.min(devicePixelRatio||1,1.5,2200/this.width,1600/this.height);
      this.canvas.width=Math.round(this.width*this.dpr);this.canvas.height=Math.round(this.height*this.dpr);
      this.scale=Math.max(this.width/W,this.height/H);this.offsetX=(this.width-W*this.scale)/2;this.offsetY=(this.height-H*this.scale)/2;
      this.resizePending=false;
    }
    gradient(a,b,dark,light){
      const flip=this.sunDirection>0&&a[0]<b[0]||this.sunDirection<0&&a[0]>b[0];
      if(flip){const first=a;a=b;b=first;}
      if(this.warmth&&/^#[0-9a-f]{6}$/i.test(light)){const rgb=[1,3,5].map(at=>parseInt(light.slice(at,at+2),16)),warm=[192,142,106];light=`rgb(${rgb.map((v,i)=>Math.round(lerp(v,warm[i],this.warmth*.25))).join(' ')})`;}
      const g=this.ctx.createLinearGradient(a[0],a[1],b[0],b[1]);g.addColorStop(0,light);g.addColorStop(.14,light);g.addColorStop(.55,dark);g.addColorStop(1,dark);return g;}
    limb(a,b,ra,rb,dark,light){
      const c=this.ctx,dx=b[0]-a[0],dy=b[1]-a[1],len=Math.max(.01,Math.hypot(dx,dy)),nx=-dy/len,ny=dx/len;
      c.fillStyle=this.gradient(point(a[0]+nx*ra,a[1]+ny*ra),point(a[0]-nx*ra,a[1]-ny*ra),dark,light);
      c.beginPath();c.moveTo(a[0]+nx*ra,a[1]+ny*ra);
      c.bezierCurveTo(a[0]+nx*ra+dx*.3,a[1]+ny*ra+dy*.3,b[0]+nx*rb-dx*.18,b[1]+ny*rb-dy*.18,b[0]+nx*rb,b[1]+ny*rb);
      c.quadraticCurveTo(b[0]+dx/len*rb,b[1]+dy/len*rb,b[0]-nx*rb,b[1]-ny*rb);
      c.bezierCurveTo(b[0]-nx*rb-dx*.18,b[1]-ny*rb-dy*.18,a[0]-nx*ra+dx*.3,a[1]-ny*ra+dy*.3,a[0]-nx*ra,a[1]-ny*ra);
      c.quadraticCurveTo(a[0]-dx/len*ra,a[1]-dy/len*ra,a[0]+nx*ra,a[1]+ny*ra);c.fill();
    }
    oval(x,y,rx,ry,fill,rotation=0){const c=this.ctx;c.fillStyle=fill;c.beginPath();c.ellipse(x,y,rx,ry,rotation,0,TAU);c.fill();}
    shadow(x,y,rx,ry,alpha){const c=this.ctx;c.save();c.translate(x,y);c.scale(rx,ry);const g=c.createRadialGradient(0,0,0,0,0,1);g.addColorStop(0,`rgba(37,34,28,${alpha})`);g.addColorStop(1,'rgba(37,34,28,0)');this.oval(0,0,1,1,g);c.restore();}
    rod(rod,g,time){
      const c=this.ctx,pw=.027*g.ppm,fw=g.flagWidth,fh=fw*2/3,angle=rod.angle;
      this.limb(rod.base,rod.top,pw/2,pw/2,'#75796b','#c7bea0');
      const down=[-Math.sin(angle),Math.cos(angle)], across=[Math.cos(angle),Math.sin(angle)],droop=Math.sin(angle)*.7;
      const cloth=(u,v)=>{
        const first=Math.sin(u*8.2-time*2.15+rod.index*1.7),second=Math.sin(u*17.1-time*3.1+rod.index*1.7*1.7);
        const ripple=(first*fw*.042+second*fw*.013)*Math.pow(u,.8);
        const dx=pw*.50+u*fw*(.91-droop*.36)+first*u*fw*.017;
        const dy=12+v*fh+ripple*(.72+v*.28)+fw*.065*u*u;
        return [rod.top[0]+across[0]*dx+down[0]*dy*(1-droop),rod.top[1]+across[1]*dx+down[1]*dy*(1-droop)+dy*droop+fw*.13*droop*u*u];
      };
      // Two continuous pieces of fabric. Smooth colour stops shade broad
      // folds without the antialiased seams of a 28-by-8 quad grid.
      const cols=36;
      for(let band=0;band<2;band++){
        const v0=band*.5,v1=band===0?.501:1;
        const gradient=c.createLinearGradient(...cloth(0,.5),...cloth(1,.5));
        const rgb=band===0?[174,57,38]:[208,166,65];
        for(let stop=0;stop<=20;stop++){
          const u=stop/20,shade=.86+Math.cos(u*8.2-time*2.15+rod.index*1.7)*.10+Math.cos(u*17.1-time*3.1)*.025;
          gradient.addColorStop(u,`rgb(${rgb.map(n=>Math.round(n*shade)).join(' ')})`);
        }
        c.fillStyle=gradient;c.beginPath();
        for(let col=0;col<=cols;col++){const q=cloth(col/cols,v0);col?c.lineTo(...q):c.moveTo(...q);}
        for(let col=cols;col>=0;col--)c.lineTo(...cloth(col/cols,v1));
        c.closePath();c.fill();
      }
    }

    // Select one authored pose. Stops and starts use the same final/first28cm
    // as the authored transition clips; no instant walk-to-idle substitution.
    // Walks wrap; pickups and start/stop clips include both endpoints.
    pose(p){
      const m=this.manifest;if(!m||this.assetState!=='ready')return null;
      const owned=p.rods.filter(Boolean).length;
      const pickup=p.action==='first-pickup'?0:p.action==='second-pickup'?1:-1;
      const first=p.flags[0]-.20*p.height,second=p.flags[1]-.20*p.height,transition=.16*p.height;
      const cycles=(p.x-first)/((m.strideMetres||1.28)*p.ppm);
      let clip=`walk${owned}`,t=((cycles%1)+1)%1,inclusive=false;
      if(pickup>=0){clip=`pickup${pickup+1}`;t=clamp((this.phase-PICKUPS[pickup].start)/(PICKUPS[pickup].end-PICKUPS[pickup].start));inclusive=true;}
      else if(p.action==='arriving'&&first-p.x<=transition){clip='stop0';t=1-(first-p.x)/transition;inclusive=true;}
      else if(p.action==='crossing'&&p.x-first<=transition){clip='start1';t=(p.x-first)/transition;inclusive=true;}
      else if(p.action==='crossing'&&second-p.x<=transition){clip='stop1';t=1-(second-p.x)/transition;inclusive=true;}
      else if(p.action==='departing'&&p.x-second<=transition){clip='start2';t=(p.x-second)/transition;inclusive=true;}
      const ids=m.clips[clip];
      const index=inclusive?Math.round(clamp(t)*(ids.length-1)):Math.floor(t*ids.length)%ids.length;
      const id=ids[index],frame=m.frames[id],scale=p.ppm*m.metresPerPixel;
      const x=p.x-m.origin[0]*scale,y=GROUND-m.origin[1]*scale;
      const project=q=>[x+q[0]*scale,y+q[1]*scale];
      return {clip,id,index,frame,scale,x,y,hands:frame.hands.map(project),feet:frame.feet.map(project)};
    }
    spriteRods(p,pose){
      return p.rods.map((rod,index)=>{
        if(!rod)return null;
        const grip=pose.hands[index],angle=pose.frame.poleAngles[index]??rod.angle;
        const axis=[Math.sin(angle),-Math.cos(angle)];
        const base=[grip[0]-axis[0]*p.gripHeight,grip[1]-axis[1]*p.gripHeight];
        const top=[base[0]+axis[0]*p.poleHeight,base[1]+axis[1]*p.poleHeight];
        return {...rod,grip,angle,base,top};
      });
    }
    person(p,pose){
      const c=this.ctx,h=p.height,m=this.manifest;
      const sun=Number(this.scene.dataset.sunX),sunX=Number.isFinite(sun)?sun*W:W*.5;
      const away=clamp((p.x-sunX)/(W*.24),-1,1);
      const sunY=Number(this.scene.dataset.sunY),lowSun=Number.isFinite(sunY)?smooth((sunY-.12)/.36):.7;
      const cast=lerp(.12,.35,lowSun)*h,shadowX=p.x+away*cast*.5;
      this.scene.dataset.patrolShadowSourceX=shadowX.toFixed(2);
      this.scene.dataset.patrolRimWarmth=this.warmth.toFixed(3);
      this.shadow(shadowX,GROUND+2+cast*.035,h*.10+Math.abs(away)*cast*.5,h*.025,.24);
      pose.feet.forEach(foot=>this.shadow(foot[0],GROUND+1,h*.038,h*.008,.36*Math.exp(-Math.max(0,GROUND-foot[1])/(h*.035))));
      // The small cached frame isolates source-atop grading to the actor alpha.
      // No tint rectangle, second body, or extra WebGL context reaches the scene.
      if(!this.spriteCanvas){this.spriteCanvas=document.createElement('canvas');this.spriteCanvas.width=m.width;this.spriteCanvas.height=m.height;this.spriteCtx=this.spriteCanvas.getContext('2d',{alpha:true});}
      const b=this.spriteCtx;if(!b)return;
      b.clearRect(0,0,m.width,m.height);b.globalCompositeOperation='source-over';
      // Packed transparent borders are restored into the original frame. All
      // body, palm, sole and ground-origin coordinates remain untrimmed.
      const [left,top,width,height]=pose.frame.crop||[0,0,m.width,m.height];
      b.drawImage(this.atlases[pose.frame.atlas],pose.frame.x,pose.frame.y,width,height,left,top,width,height);
      if(this.warmth>0){b.globalCompositeOperation='source-atop';b.fillStyle=`rgba(220,113,59,${this.warmth*.22})`;b.fillRect(0,0,m.width,m.height);b.globalCompositeOperation='source-over';}
      c.drawImage(this.spriteCanvas,pose.x,pose.y,m.width*pose.scale,m.height*pose.scale);
    }
    draw(){
      if(!this.drawable()){this.pending=true;return;}
      if(this.resizePending)this.applySize();
      const c=this.ctx;c.setTransform(this.dpr,0,0,this.dpr,0,0);c.clearRect(0,0,this.width,this.height);
      const p=sample(this.phase,this.mode,Number(this.scene.dataset.coastHorizon)||.507);
      const pose=p.active?this.pose(p):null;
      const render=p.active&&!this.reduced()&&!!pose;
      const sun=Number(this.scene.dataset.sunX);this.sunDirection=Number.isFinite(sun)?Math.sign(sun*W-(p.x||W*.5)):0;
      this.warmth=smooth((this.phase-.40)/.07)*(1-smooth((this.phase-.50)/.07));
      if(render){
        c.save();c.setTransform(this.dpr*this.scale,0,0,this.dpr*this.scale,this.dpr*this.offsetX,this.dpr*this.offsetY);
        // Subpixel optical softness helps the small figure sit in the photo.
        c.filter=`blur(${(.28/Math.max(.3,this.scale)).toFixed(3)}px)`;
        c.globalAlpha=1-smooth((this.night-.82)/.18)*.25;
        const fabricTime=Number(this.scene.dataset.lifeTime)||this.phase*64;
        const rods=this.spriteRods(p,pose);
        rods.forEach(rod=>{if(rod)this.rod(rod,p,fabricTime);});
        this.person(p,pose);
        this.scene.dataset.patrolClip=pose.clip;this.scene.dataset.patrolSpriteFrame=String(pose.id);
        this.scene.dataset.patrolHands=JSON.stringify(pose.hands.map(q=>q.map(n=>Number(n.toFixed(3)))));
        this.scene.dataset.patrolRodGrips=JSON.stringify(rods.map(r=>r?r.grip.map(n=>Number(n.toFixed(3))):null));
        this.scene.dataset.patrolRodBases=JSON.stringify(rods.map(r=>r?r.base.map(n=>Number(n.toFixed(3))):null));c.restore();
      }
      this.rendered=render;this.pending=false;this.frames++;
      this.scene.dataset.patrolFrame=String(this.frames);this.scene.dataset.patrolPhase=this.phase.toFixed(5);
      this.scene.dataset.patrolAction=render?p.action:'absent';this.scene.dataset.patrolSourceX=p.active?p.x.toFixed(2):'';
      this.scene.dataset.patrolHeight=p.height.toFixed(2);this.scene.dataset.patrolCarried=render?String(p.rods.filter(Boolean).length):'0';
      this.canvas.dataset.patrolFrame=String(this.frames);
    }
    suspend(){this.suspended=true;this.sync();}
    resume(){this.suspended=false;this.pending=true;this.lastDraw=-Infinity;this.sync();}
    destroy(){if(this.destroyed)return;this.destroyed=true;this.loadVersion++;this.loadAbort?.abort();this.assetImages?.forEach(image=>{image.onload=null;image.onerror=null;});this.atlases=[];this.spriteCanvas=null;this.spriteCtx=null;cancelAnimationFrame(this.raf);this.raf=0;this.cleanups.forEach(fn=>fn());this.io?.disconnect();this.ro?.disconnect();this.canvas.remove();instances.delete(this.scene);this.scene.dataset.patrolState='destroyed';}
  }
  function scan(scope){
    const scenes=[...(scope.querySelectorAll?.('[data-coastal-section] .coast-scene')||[])];
    if(scope.matches?.('.coast-scene'))scenes.unshift(scope);
    scenes.forEach(scene=>{if(!instances.has(scene))instances.set(scene,new Patrol(scene));});
  }
  window.CgauCoastalPatrol={scan,flagStanding,sample,geometry,pickups:PICKUPS,flagStandingForScene:(scene,index,phase,mode='cycle')=>{
    const instance=instances.get(scene);
    return mode==='cycle'&&(!instance||instance.assetState!=='ready'||instance.reduced())?true:flagStanding(index,phase,mode);
  },version:'v3-authored-patrol-1',destroyAll:()=>[...instances.values()].forEach(x=>x.destroy())};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>scan(document),{once:true});else scan(document);
  document.addEventListener('shopify:section:load',event=>scan(event.target));
  document.addEventListener('shopify:section:unload',event=>[...instances.values()].filter(x=>event.target.contains(x.scene)).forEach(x=>x.destroy()));
  window.addEventListener('pagehide',event=>[...instances.values()].forEach(x=>event.persisted?x.suspend():x.destroy()));
  window.addEventListener('pageshow',()=>{scan(document);instances.forEach(x=>x.resume());});
})();
